import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['your-daily-freedom-number'];

export default function YourDailyFreedomNumberPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['from-sign-up-to-a-real-fire-plan', 'connect-everything-even-the-weird-stuff']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'The daily freedom number is Per Diem’s answer to the question most people actually ask: what can I spend today and still stay on track?',
          'It works because budgets, balances, and FIRE projections all feed the same model instead of living in separate tools.',
          'A daily number is more actionable than a retirement chart alone because it translates the long-term plan into a decision you can use right now.',
        ]}
      />

      <ArticleSection title="Most financial tools are better at describing than deciding">
        <ArticleParagraph>
          Personal finance software usually gives you one of two things. It either tells
          you what already happened, like a transaction feed or net worth dashboard, or it
          gives you a long-term projection that feels disconnected from everyday life.
        </ArticleParagraph>
        <ArticleParagraph>
          Both are useful, but neither is the question most people carry with them through
          the week. The practical question is simpler: can I spend this money and still be okay?
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem is built around that question. The product turns a complex household plan
          into a daily spending number meant to be understandable at a glance.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileHome}
        alt="Per Diem mobile home screen with daily spending number"
        caption="The headline number is meant to turn a broad financial picture into something a household can actually act on today."
        variant="phone"
      />

      <ArticleSection title="Why a daily number matters">
        <ArticleParagraph>
          A retirement projection can tell you whether your assumptions work in aggregate.
          A daily number helps you live inside those assumptions without needing to rerun the
          entire model in your head every time you make a decision.
        </ArticleParagraph>
        <ArticleBullets
          items={[
            'It gives the household a shared decision metric instead of a vague feeling about whether spending is “fine.”',
            'It makes tradeoffs visible earlier, before they quietly compound into a very different FIRE timeline.',
            'It reduces the gap between short-term behavior and long-term planning.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="The number only works if the rest of the product is connected">
        <ArticleParagraph>
          A daily spending number would be meaningless if it were just a budgeting gimmick.
          The reason it matters in Per Diem is that the model is informed by the same data the
          rest of the household uses: accounts, transactions, budgets, income, liabilities,
          and the long-term FIRE plan.
        </ArticleParagraph>
        <ArticleParagraph>
          That means the number is not standing alone. It is the compressed expression of a
          larger financial model.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileBudget}
        alt="Per Diem mobile budget screen"
        caption="The budget matters because the daily number should come from the household’s actual spending plan, not from an abstract rule of thumb."
        variant="phone"
      />

      <ArticleSection title="This is where budgeting and planning stop being separate jobs">
        <ArticleParagraph>
          One of the core frustrations in this category is that budgeting products and retirement
          planning products often live in different systems. One tells you what category you spent
          from. The other tells you whether you can retire in twelve years. The household is left
          to figure out how those two facts relate.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem is trying to close that gap. When a spending choice changes the household’s
          long-term picture, the product should be able to reflect that without requiring a separate
          spreadsheet ritual.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileFirePlan}
        alt="Per Diem mobile FIRE plan"
        caption="The FIRE plan provides the deeper model. The daily number is the everyday interface to that model."
        variant="phone"
      />

      <ArticleSection title="A good daily number changes behavior because it feels believable">
        <ArticleParagraph>
          The point is not to make the number artificially strict. The point is to make it credible.
          Households do not want a motivational slogan. They want a number they can trust enough to
          discuss, challenge, and use.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why connected planning matters so much. If the number is informed by real balances,
          real spending patterns, and a real long-term plan, it becomes easier to treat it like a
          useful operating metric instead of a novelty.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="This is the product idea in one sentence">
        <ArticleParagraph>
          Plenty of apps help you look at your money. Per Diem is trying to help you decide what
          to do with it today while still respecting the life you are trying to build over the next
          ten or twenty years.
        </ArticleParagraph>
        <ArticleParagraph>
          That is why the daily freedom number sits at the center of the experience. It is not a
          random metric. It is the simplest expression of the whole product thesis.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
